"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Zap, CheckCircle2, CircleCheck, Loader, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-white">Solicitação Enviada</h3>
        <p className="text-white/70 max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full px-8 border-primary text-primary hover:bg-primary/5">
          Enviar Outra Solicitação
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Nome é obrigatório",
        minLength: {
          value: 2,
          message: "O nome deve ter pelo menos 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-xs uppercase tracking-wider text-white/60 font-semibold">Nome Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ex: João Silva" className="bg-white/5 border-white/10 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300 text-white" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mail é obrigatório",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Por favor, insira um e-mail válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-white/60 font-semibold">E-mail Corporativo</FormLabel>
              <FormControl>
                <Input type="email" placeholder="joao@empresa.com.br" className="bg-white/5 border-white/10 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300 text-white" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-white/60 font-semibold">WhatsApp / Telefone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(11) 99999-9999" className="bg-white/5 border-white/10 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300 text-white" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_description" rules={{
      required: "A descrição é necessária",
      minLength: {
        value: 10,
        message: "Por favor, forneça mais detalhes (mín. 10 caracteres)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-white/60 font-semibold">Sua Necessidade</FormLabel>
            <FormControl>
              <Textarea placeholder="Descreva seu desafio..." className="bg-white/5 border-white/10 focus-visible:ring-primary focus-visible:border-primary min-h-[100px] rounded-xl resize-none transition-all duration-300 text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-white/60 font-semibold">Faturamento Mensal (R$)</FormLabel>
            <FormControl>
              <Input type="number" placeholder="Opcional" className="bg-white/5 border-white/10 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300 text-white" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] text-white/40">Ajuda-nos a dimensionar a melhor solução para seu porte.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm">
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all duration-300 group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Enviando Solicitação...
          </> : <>
            Solicitar Diagnóstico
            <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
function ProductsCarouselConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <Carousel opts={{
      align: "start"
    }} className="w-full">
        <CarouselContent>
          {[...Array(4)].map((_, i) => <CarouselItem key={i} data-index={i} className="md:basis-1/2 lg:basis-1/3 pl-4">
              <Card className="rounded-[16px] border-none shadow-lg overflow-hidden h-full">
                <CardContent className="p-4 flex flex-col gap-4 h-full">
                  <div className="w-full aspect-square bg-muted/20 animate-pulse rounded-lg" />
                  <div className="h-6 bg-muted/20 animate-pulse rounded w-3/4" />
                  <div className="h-4 bg-muted/20 animate-pulse rounded w-1/2" />
                  <div className="mt-auto h-10 bg-muted/20 animate-pulse rounded w-full" />
                </CardContent>
              </Card>
            </CarouselItem>)}
        </CarouselContent>
      </Carousel>;
  }
  if (error || products.length === 0) {
    return <div data-section-id="155828" className="text-secondary-foreground/80 text-center p-8 bg-card rounded-2xl border border-white/10">
        <p>Nenhuma solução Linx disponível para exibição no momento.</p>
      </div>;
  }
  return <Carousel opts={{
    align: "start"
  }} className="w-full">
      <CarouselContent className="-ml-4">
        {products.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
            <Card className="rounded-[16px] border border-white/10 shadow-2xl bg-card text-card-foreground h-full flex flex-col overflow-hidden transition-all hover:-translate-y-2 duration-300">
              <CardContent className="p-4 flex flex-col flex-grow gap-4">
                <Link to={product.link} className="block group">
                  <AspectRatio ratio={1} className="bg-muted rounded-lg overflow-hidden">
                    {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-accent/10 text-accent">
                        <span className="text-sm font-medium">Sem imagem</span>
                      </div>}
                  </AspectRatio>
                </Link>
                
                <div className="flex flex-col flex-grow">
                  <Link to={product.link} className="hover:underline decoration-primary underline-offset-2">
                    <h3 className="font-sans font-semibold text-base line-clamp-2 leading-tight">
                      {product.title}
                    </h3>
                  </Link>
                  
                  {product.price !== undefined && <div className="mt-2 mb-4">
                      <span className="text-lg font-bold text-primary">
                        R$ {product.price.toFixed(2)}
                      </span>
                    </div>}
                  
                  <div className="mt-auto pt-4">
                    <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-lg font-bold shadow-lg hover:bg-primary/90 transition-all uppercase tracking-wider text-xs" text="Solicitar Demonstração" showQuantity={false} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </CarouselItem>)}
      </CarouselContent>
      <div className="hidden md:block">
        <CarouselPrevious className="bg-card text-foreground hover:bg-card border border-white/10 shadow-lg -left-4" />
        <CarouselNext className="bg-card text-foreground hover:bg-card border border-white/10 shadow-lg -right-4" />
      </div>
    </Carousel>;
}
export default function SalesBoost() {
  const wp_query_params = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section className="relative rounded-3xl bg-background p-8 md:p-12 lg:p-16 shadow-2xl overflow-hidden border border-white/5">
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-slate-800/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-slate-900/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm border border-white/10">
                <Zap className="size-4 text-primary" />
                <span>Transformação Digital</span>
              </div>

              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl leading-tight">
                Pronto para escalar sua operação com tecnologia de ponta?
              </h2>

              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Nossos especialistas em ERP e soluções digitais estão prontos para transformar sua gestão. Dê o próximo passo rumo à eficiência total.
              </p>

              <ul className="space-y-4">
                {["Consultoria especializada em Microvix e e Millennium", "Otimização de processos e gestão empresarial", "Suporte técnico focado em alta performance"].map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-white/80">
                    <CheckCircle2 className="size-6 text-primary shrink-0" />
                    <span className="text-base">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            <div className="rounded-2xl bg-card p-6 md:p-8 border border-white/5 shadow-xl">
              <Card className="bg-transparent border-none shadow-none p-0 gap-0">
                <CardHeader className="p-0 mb-6">
                  <CardTitle className="text-2xl font-semibold text-white">
                    Transforme sua gestão agora
                  </CardTitle>
                  <CardDescription className="text-sm text-white/60 mt-1">
                    Preencha o formulário e receba um diagnóstico especializado da nossa equipe.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <FormProvider formId="bottom_cta_microvix" sectionName="SalesBoost" defaultValues={{
              name: "",
              email: "",
              phone: "",
              project_description: "",
              budget: ""
            }}>
                    <InquiryFormInner />
                  </FormProvider>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>;
}